import { useEffect, useState } from "react";
import { Trophy, Loader2, Plus, Trash2, Check, Globe, Settings2, Calendar, Users, ListOrdered, Save } from "lucide-react";
import { endpoints, apiError } from "@/lib/api";
import { toast } from "sonner";
import { AdminFbHighlights } from "./AdminFbHighlights";
import { ScoreBatKeyPanel } from "./ScoreBatKeyPanel";

const Input = (p) => <input {...p} className={`h-10 px-3 rounded-lg bg-[#0A0C10] border border-[#273042] text-white text-sm placeholder-slate-500 outline-none focus:border-[#E11D2A] ${p.className || ""}`} />;
const Btn = ({ children, ...p }) => <button {...p} className={`h-10 px-4 rounded-lg text-sm font-semibold ${p.className || "bg-[#E11D2A] text-white"}`}>{children}</button>;

export default function AdminFootball() {
  const [comps, setComps] = useState([]);
  const [sel, setSel] = useState(null);
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newComp, setNewComp] = useState({ name: "", country: "Rwanda", scope: "local" });
  const [area, setArea] = useState("competitions");

  const loadComps = () => endpoints.adminFbCompetitions().then((r) => { setComps(r.data?.competitions || []); }).finally(() => setLoading(false));
  useEffect(() => { loadComps(); endpoints.adminFbProviderConfig().then((r) => setProvider(r.data)).catch(() => {}); }, []);

  const createComp = async () => {
    if (!newComp.name.trim()) return toast.error("Enter a competition name");
    try { await endpoints.adminFbCreateCompetition({ ...newComp, provider: newComp.scope === "international" ? "api-football" : "manual", published: true }); toast.success("Competition created."); setNewComp({ name: "", country: "Rwanda", scope: "local" }); loadComps(); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  };
  const togglePub = async (c) => { await endpoints.adminFbUpdateCompetition(c.id, { published: !c.published }); loadComps(); };
  const delComp = async (c) => { if (!window.confirm(`Delete ${c.name}? This removes its teams, fixtures and standings.`)) return; await endpoints.adminFbDeleteCompetition(c.id); setSel(null); loadComps(); };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-[#E11D2A]" /></div>;

  return (
    <div data-testid="admin-football-page">
      <h1 className="text-2xl font-extrabold text-white flex items-center gap-2 mb-1"><Trophy className="w-6 h-6 text-[#E11D2A]" /> Football / Leagues</h1>
      <p className="text-slate-400 text-sm mb-5">Manage competitions, teams, fixtures, results and standings.</p>

      {provider && (
        <div className="rounded-xl bg-[#121620] border border-[#273042] p-4 mb-6 flex items-start gap-3" data-testid="provider-config">
          <Globe className="w-5 h-5 text-[#E11D2A] mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="text-white font-semibold">International data provider (API-Football): <span className={provider.configured ? "text-green-400" : "text-amber-400"}>{provider.configured ? "Connected" : "Not configured"}</span></p>
            <p className="text-slate-400 text-xs mt-1">{provider.note}</p>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-5">
        {[["competitions", "Competitions"], ["highlights", "Highlights"]].map(([k, l]) => (
          <button key={k} onClick={() => setArea(k)} data-testid={`admin-fb-area-${k}`}
            className={`h-9 px-4 rounded-full text-sm font-semibold ${area === k ? "bg-[#E11D2A] text-white" : "bg-[#121620] border border-[#273042] text-slate-300"}`}>{l}</button>
        ))}
      </div>
      {area === "highlights" ? <><ScoreBatKeyPanel /><AdminFbHighlights /></> : (
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Competitions list + create */}
        <div className="lg:col-span-1 space-y-3">
          <div className="rounded-xl bg-[#121620] border border-[#273042] p-4">
            <p className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Plus className="w-4 h-4 text-[#E11D2A]" /> New competition</p>
            <Input value={newComp.name} onChange={(e) => setNewComp({ ...newComp, name: e.target.value })} data-testid="new-comp-name" placeholder="Competition name" className="w-full mb-2" />
            <Input value={newComp.country} onChange={(e) => setNewComp({ ...newComp, country: e.target.value })} placeholder="Country" className="w-full mb-2" />
            <select value={newComp.scope} onChange={(e) => setNewComp({ ...newComp, scope: e.target.value })} className="w-full h-10 px-3 mb-3 rounded-lg bg-[#0A0C10] border border-[#273042] text-white text-sm">
              <option value="local">Local (manual)</option>
              <option value="international">International (API)</option>
            </select>
            <Btn onClick={createComp} data-testid="create-comp-btn" className="w-full bg-[#E11D2A] text-white">Create</Btn>
          </div>
          {comps.map((c) => (
            <button key={c.id} onClick={() => setSel(c)} data-testid={`comp-item-${c.id}`}
              className={`w-full text-left rounded-xl p-3.5 border transition-colors ${sel?.id === c.id ? "bg-[#E11D2A]/10 border-[#E11D2A]/60" : "bg-[#121620] border-[#273042] hover:border-[#E11D2A]/40"}`}>
              <p className="text-white font-semibold text-sm flex items-center gap-2">{c.scope === "international" ? <Globe className="w-4 h-4 text-slate-400" /> : <Trophy className="w-4 h-4 text-[#E11D2A]" />} {c.name}</p>
              <p className="text-xs text-slate-500 mt-0.5">{c.country} · {c.teamCount || 0} teams · {c.published ? "Published" : "Hidden"}</p>
            </button>
          ))}
        </div>

        {/* Management panel */}
        <div className="lg:col-span-2">
          {!sel ? <div className="rounded-xl bg-[#121620] border border-[#273042] p-10 text-center text-slate-500">Select a competition to manage it.</div>
            : sel.scope === "international" ? <IntlPanel comp={sel} onToggle={() => togglePub(sel)} onDelete={() => delComp(sel)} provider={provider} />
            : <ManagePanel key={sel.id} comp={sel} onToggle={() => togglePub(sel)} onDelete={() => delComp(sel)} refreshList={loadComps} />}
        </div>
      </div>
      )}
    </div>
  );
}

function IntlPanel({ comp, onToggle, onDelete, provider }) {
  return (
    <div className="rounded-xl bg-[#121620] border border-[#273042] p-6" data-testid="intl-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white flex items-center gap-2"><Globe className="w-5 h-5 text-[#E11D2A]" /> {comp.name}</h2>
        <div className="flex gap-2">
          <Btn onClick={onToggle} className="bg-white/5 border border-[#273042] text-slate-200">{comp.published ? "Unpublish" : "Publish"}</Btn>
          <Btn onClick={onDelete} className="bg-red-600/20 text-red-400"><Trash2 className="w-4 h-4" /></Btn>
        </div>
      </div>
      <p className="text-slate-400 text-sm">This is an international competition served live from the API-Football provider (league ID {comp.apiLeagueId}).</p>
      <p className={`text-sm mt-3 font-semibold ${provider?.configured ? "text-green-400" : "text-amber-400"}`}>
        {provider?.configured ? "Live data is connected — standings, fixtures and results show automatically." : "Awaiting provider: add API_FOOTBALL_KEY in Admin → Secrets, then republish. Until then this competition shows an 'awaiting provider' notice to customers (no invented data)."}
      </p>
    </div>
  );
}

function ManagePanel({ comp, onToggle, onDelete, refreshList }) {
  const [sub, setSub] = useState("fixtures");
  const [seasons, setSeasons] = useState([]);
  const [teams, setTeams] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [standings, setStandings] = useState([]);
  const [seasonId, setSeasonId] = useState("");
  const [newSeason, setNewSeason] = useState("");
  const [newTeam, setNewTeam] = useState({ name: "", logoUrl: "" });
  const [newFix, setNewFix] = useState({ matchday: 1, homeTeamId: "", awayTeamId: "", date: "", venue: "" });
  const [newAdj, setNewAdj] = useState({ teamId: "", points: "", reason: "" });

  const loadAll = () => {
    endpoints.adminFbSeasons(comp.id).then((r) => {
      const ss = r.data?.seasons || []; setSeasons(ss);
      const cur = ss.find((s) => s.isCurrent) || ss[0];
      if (cur && !seasonId) setSeasonId(cur.id);
    });
    endpoints.adminFbTeams(comp.id).then((r) => setTeams(r.data?.teams || []));
  };
  useEffect(() => { loadAll(); }, [comp.id]);
  useEffect(() => {
    if (!seasonId) return;
    endpoints.adminFbFixtures(comp.id, seasonId).then((r) => setFixtures(r.data?.fixtures || []));
    endpoints.adminFbAdjustments(comp.id, seasonId).then((r) => setAdjustments(r.data?.adjustments || []));
    endpoints.adminFbStandingsPreview(comp.id, seasonId).then((r) => setStandings(r.data?.standings || [])).catch(() => {});
  }, [seasonId, comp.id]);

  const refresh = () => { if (seasonId) { endpoints.adminFbFixtures(comp.id, seasonId).then((r) => setFixtures(r.data?.fixtures || [])); endpoints.adminFbStandingsPreview(comp.id, seasonId).then((r) => setStandings(r.data?.standings || [])).catch(() => {}); endpoints.adminFbAdjustments(comp.id, seasonId).then((r) => setAdjustments(r.data?.adjustments || [])); } };
  const wrap = (p) => p.then(() => { }).catch((e) => toast.error(apiError(e?.response?.data?.detail)));

  const addSeason = async () => { if (!newSeason.trim()) return; await endpoints.adminFbCreateSeason(comp.id, { name: newSeason, isCurrent: seasons.length === 0 }); setNewSeason(""); loadAll(); };
  const addTeam = async () => { if (!newTeam.name.trim()) return; try { await endpoints.adminFbCreateTeam(comp.id, newTeam); setNewTeam({ name: "", logoUrl: "" }); endpoints.adminFbTeams(comp.id).then((r) => setTeams(r.data?.teams || [])); refreshList(); } catch (e) { toast.error(apiError(e?.response?.data?.detail)); } };
  const uploadLogo = async (file) => { try { const r = await endpoints.adminUploadImage(file); const url = r.data?.url || r.data?.dataUrl || r.data?.image || ""; setNewTeam((t) => ({ ...t, logoUrl: url })); toast.success("Logo uploaded"); } catch { toast.error("Upload failed"); } };
  const delTeam = async (id) => { await endpoints.adminFbDeleteTeam(id); endpoints.adminFbTeams(comp.id).then((r) => setTeams(r.data?.teams || [])); };
  const addFixture = async () => {
    if (!seasonId) return toast.error("Create a season first");
    if (!newFix.homeTeamId || !newFix.awayTeamId) return toast.error("Pick both teams");
    try { await endpoints.adminFbCreateFixture(comp.id, { ...newFix, seasonId }); setNewFix({ matchday: newFix.matchday, homeTeamId: "", awayTeamId: "", date: "", venue: "" }); refresh(); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  };
  const enterResult = async (f, hs, as_) => { await wrap(endpoints.adminFbEnterResult(f.id, { homeScore: Number(hs), awayScore: Number(as_) })); refresh(); };
  const delFixture = async (id) => { await endpoints.adminFbDeleteFixture(id); refresh(); };
  const addAdj = async () => { if (!newAdj.teamId) return toast.error("Pick a team"); await endpoints.adminFbAddAdjustment(comp.id, { ...newAdj, seasonId, points: Number(newAdj.points || 0) }); setNewAdj({ teamId: "", points: "", reason: "" }); refresh(); };
  const delAdj = async (id) => { await endpoints.adminFbDeleteAdjustment(id); refresh(); };

  const SUBS = [["fixtures", "Fixtures & Results", Calendar], ["teams", "Teams", Users], ["standings", "Standings", ListOrdered], ["adjustments", "Adjustments", Settings2]];
  const teamName = (id) => teams.find((t) => t.id === id)?.name || "—";

  return (
    <div className="rounded-xl bg-[#121620] border border-[#273042] p-5" data-testid="manage-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-bold text-white flex items-center gap-2"><Trophy className="w-5 h-5 text-[#E11D2A]" /> {comp.name}</h2>
        <div className="flex gap-2">
          <Btn onClick={onToggle} className="bg-white/5 border border-[#273042] text-slate-200">{comp.published ? "Unpublish" : "Publish"}</Btn>
          <Btn onClick={onDelete} className="bg-red-600/20 text-red-400"><Trash2 className="w-4 h-4" /></Btn>
        </div>
      </div>

      {/* Season selector */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select value={seasonId} onChange={(e) => setSeasonId(e.target.value)} data-testid="season-select" className="h-10 px-3 rounded-lg bg-[#0A0C10] border border-[#273042] text-white text-sm">
          {seasons.length === 0 && <option value="">No seasons</option>}
          {seasons.map((s) => <option key={s.id} value={s.id}>{s.name}{s.isCurrent ? " (current)" : ""}</option>)}
        </select>
        <Input value={newSeason} onChange={(e) => setNewSeason(e.target.value)} data-testid="new-season" placeholder="2025/26" className="w-28" />
        <Btn onClick={addSeason} data-testid="add-season-btn" className="bg-white/5 border border-[#273042] text-slate-200">Add season</Btn>
      </div>

      <div className="flex gap-1 mb-5 border-b border-[#273042] overflow-x-auto">
        {SUBS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setSub(k)} data-testid={`sub-${k}`}
            className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px whitespace-nowrap flex items-center gap-1.5 ${sub === k ? "text-white border-[#E11D2A]" : "text-slate-400 border-transparent hover:text-white"}`}><Icon className="w-3.5 h-3.5" /> {l}</button>
        ))}
      </div>

      {sub === "teams" && (
        <div data-testid="teams-section">
          <div className="flex gap-2 mb-4 flex-wrap items-center">
            <Input value={newTeam.name} onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })} data-testid="team-name" placeholder="Team name" className="flex-1 min-w-[140px]" />
            <Input value={newTeam.logoUrl} onChange={(e) => setNewTeam({ ...newTeam, logoUrl: e.target.value })} placeholder="Logo URL (optional)" className="flex-1 min-w-[140px]" />
            <label className="h-10 px-3 rounded-lg bg-white/5 border border-[#273042] text-slate-200 text-sm flex items-center cursor-pointer">Upload<input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadLogo(e.target.files[0])} /></label>
            <Btn onClick={addTeam} data-testid="add-team-btn"><Plus className="w-4 h-4" /></Btn>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {teams.map((t) => (
              <div key={t.id} className="flex items-center gap-2 bg-[#0A0C10] border border-[#273042] rounded-lg px-3 py-2" data-testid={`team-${t.id}`}>
                {t.logoUrl ? <img src={t.logoUrl} alt="" className="w-6 h-6 object-contain" /> : <Users className="w-5 h-5 text-slate-500" />}
                <span className="text-white text-sm flex-1">{t.name}</span>
                <button onClick={() => delTeam(t.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {teams.length === 0 && <p className="text-slate-500 text-sm">No teams yet.</p>}
          </div>
        </div>
      )}

      {sub === "fixtures" && (
        <div data-testid="fixtures-section">
          <div className="rounded-lg bg-[#0A0C10] border border-[#273042] p-3 mb-4 grid sm:grid-cols-6 gap-2 items-center">
            <Input type="number" value={newFix.matchday} onChange={(e) => setNewFix({ ...newFix, matchday: e.target.value })} placeholder="MD" className="w-full" />
            <select value={newFix.homeTeamId} onChange={(e) => setNewFix({ ...newFix, homeTeamId: e.target.value })} data-testid="fix-home" className="h-10 px-2 rounded-lg bg-[#0A0C10] border border-[#273042] text-white text-sm"><option value="">Home</option>{teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
            <select value={newFix.awayTeamId} onChange={(e) => setNewFix({ ...newFix, awayTeamId: e.target.value })} data-testid="fix-away" className="h-10 px-2 rounded-lg bg-[#0A0C10] border border-[#273042] text-white text-sm"><option value="">Away</option>{teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
            <Input type="date" value={newFix.date} onChange={(e) => setNewFix({ ...newFix, date: e.target.value })} className="w-full" />
            <Input value={newFix.venue} onChange={(e) => setNewFix({ ...newFix, venue: e.target.value })} placeholder="Venue" className="w-full" />
            <Btn onClick={addFixture} data-testid="add-fixture-btn">Add fixture</Btn>
          </div>
          <div className="space-y-2">
            {fixtures.map((f) => <FixtureRow key={f.id} f={f} teamName={teamName} onResult={enterResult} onDelete={delFixture} />)}
            {fixtures.length === 0 && <p className="text-slate-500 text-sm">No fixtures yet. Add some above.</p>}
          </div>
        </div>
      )}

      {sub === "standings" && (
        <div data-testid="standings-section" className="overflow-x-auto">
          <p className="text-xs text-slate-500 mb-2">Auto-calculated from finished results (3 pts win / 1 draw) + adjustments.</p>
          <table className="w-full text-sm">
            <thead className="text-slate-400"><tr><th className="text-left py-2">#</th><th className="text-left">Team</th><th>P</th><th>W</th><th>D</th><th>L</th><th>GD</th><th className="text-white">Pts</th></tr></thead>
            <tbody>
              {standings.map((r, i) => <tr key={i} className="border-t border-[#273042] text-slate-200"><td className="py-2 text-slate-500">{r.rank}</td><td>{r.team?.name}</td><td className="text-center">{r.played}</td><td className="text-center">{r.won}</td><td className="text-center">{r.drawn}</td><td className="text-center">{r.lost}</td><td className="text-center">{r.gd}</td><td className="text-center font-bold text-white">{r.points}</td></tr>)}
            </tbody>
          </table>
          {standings.length === 0 && <p className="text-slate-500 text-sm mt-2">Add teams and finished results to build the table.</p>}
        </div>
      )}

      {sub === "adjustments" && (
        <div data-testid="adjustments-section">
          <div className="flex gap-2 mb-4 flex-wrap items-center">
            <select value={newAdj.teamId} onChange={(e) => setNewAdj({ ...newAdj, teamId: e.target.value })} className="h-10 px-2 rounded-lg bg-[#0A0C10] border border-[#273042] text-white text-sm"><option value="">Team</option>{teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
            <Input type="number" value={newAdj.points} onChange={(e) => setNewAdj({ ...newAdj, points: e.target.value })} placeholder="± points" className="w-24" />
            <Input value={newAdj.reason} onChange={(e) => setNewAdj({ ...newAdj, reason: e.target.value })} placeholder="Reason" className="flex-1 min-w-[120px]" />
            <Btn onClick={addAdj} data-testid="add-adj-btn">Apply</Btn>
          </div>
          <div className="space-y-2">
            {adjustments.map((a) => (
              <div key={a.id} className="flex items-center gap-2 bg-[#0A0C10] border border-[#273042] rounded-lg px-3 py-2 text-sm">
                <span className="text-white flex-1">{teamName(a.teamId)}: <b className={a.points < 0 ? "text-red-400" : "text-green-400"}>{a.points > 0 ? "+" : ""}{a.points} pts</b> {a.reason && <span className="text-slate-500">— {a.reason}</span>}</span>
                <button onClick={() => delAdj(a.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {adjustments.length === 0 && <p className="text-slate-500 text-sm">No adjustments.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function FixtureRow({ f, teamName, onResult, onDelete }) {
  const [hs, setHs] = useState(f.homeScore ?? "");
  const [as_, setAs] = useState(f.awayScore ?? "");
  return (
    <div className="flex items-center gap-2 bg-[#0A0C10] border border-[#273042] rounded-lg px-3 py-2 flex-wrap" data-testid={`fixture-${f.id}`}>
      <span className="text-[11px] text-slate-500 w-10">MD{f.matchday}</span>
      <span className="text-white text-sm flex-1 text-right truncate">{f.home?.name || teamName(f.homeTeamId)}</span>
      <input value={hs} onChange={(e) => setHs(e.target.value)} className="w-10 h-9 text-center rounded bg-[#121620] border border-[#273042] text-white text-sm" data-testid={`hs-${f.id}`} />
      <span className="text-slate-500">:</span>
      <input value={as_} onChange={(e) => setAs(e.target.value)} className="w-10 h-9 text-center rounded bg-[#121620] border border-[#273042] text-white text-sm" data-testid={`as-${f.id}`} />
      <span className="text-white text-sm flex-1 truncate">{f.away?.name || teamName(f.awayTeamId)}</span>
      <span className={`text-[10px] px-2 py-0.5 rounded-full ${f.status === "finished" ? "bg-green-500/15 text-green-400" : "bg-slate-500/15 text-slate-400"}`}>{f.status}</span>
      <button onClick={() => onResult(f, hs, as_)} data-testid={`save-result-${f.id}`} className="h-8 px-2 rounded bg-[#E11D2A] text-white text-xs font-semibold flex items-center gap-1"><Save className="w-3.5 h-3.5" /> Result</button>
      <button onClick={() => onDelete(f.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}
